import { useParams, Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import Header from '../components/Header'
import Footer from '../components/Footer'
import AuthorAvatar from '../components/AuthorAvatar'
import { getAuthor } from '../data/authors'
import { articles, featuredArticle } from '../data/articles'

const allArticles = [featuredArticle, ...articles]

export default function AuthorDetail() {
  const { slug } = useParams()
  const author = slug ? safeGetAuthor(slug) : undefined

  if (!author) {
    return (
      <div className="os-root">
        <Head>
          <title>Author Not Found · Original Savage</title>
        </Head>
        <Header />
        <main id="main">
          <section className="wrap sec">
            <h1>That author profile doesn't exist</h1>
            <p>
              <Link to="/">Back to the homepage &rarr;</Link>
            </p>
          </section>
        </main>
        <Footer />
      </div>
    )
  }

  const byline = allArticles.filter((a) => a.authorSlug === author.slug)
  const pageUrl = `https://originalsavage.com/authors/${author.slug}`

  return (
    <div className="os-root">
      <Head>
        <title>{author.name} · Original Savage</title>
        <meta name="description" content={author.metaDescription} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            '@id': `${pageUrl}#person`,
            name: author.name,
            description: author.bio,
            jobTitle: author.role,
            knowsAbout: author.expertise,
            url: pageUrl,
          })}
        </script>
      </Head>
      <Header />
      <main id="main">
        <section className="wrap sec author-detail">
          <div className="author-detail-head">
            <AuthorAvatar author={author} size={72} />
            <div>
              <h1>{author.name}</h1>
              <p className="author-role">{author.role}</p>
              <p className="author-meta">
                {author.age} &middot; {author.location}
              </p>
            </div>
          </div>
          <p className="author-bio">{author.bio}</p>
          <p className="author-expertise">
            <b>Covers:</b> {author.expertise.join(', ')}
          </p>

          {byline.length > 0 && (
            <div className="author-articles">
              <h2>Dispatches by {author.name}</h2>
              <ul>
                {byline.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/articles/${a.slug}`}>{a.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  )
}

// Doesn't throw on an unknown slug (getAuthor does) — a bad URL should render
// the not-found state above, not crash the page.
function safeGetAuthor(slug: string) {
  try {
    return getAuthor(slug)
  } catch {
    return undefined
  }
}
